import { useEffect, useState, type PropsWithChildren } from "react";
import { ExternalLink } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import Button from "@/components/common/button/Button";
import Input from "@/components/common/input/Input";
import { acceptPolicy, completeProfile } from "@/api/complianceService";
import type { pendingDocument } from "@/types/api/user.api";
import styles from "./ComplianceGate.module.css";

/** Ruta pública donde se puede leer cada tipo de documento. */
const DOC_ROUTES: Record<string, string> = {
    policy: "/politica-de-tratamiento",
    privacy_notice: "/aviso-de-privacidad",
};

/**
 * Gate bloqueante post-login (Ley 1581).
 *
 * Si el usuario no completó su perfil (fecha de nacimiento) o tiene documentos
 * pendientes por aceptar (Política de Tratamiento, Aviso de Privacidad), muestra
 * una pantalla que no se puede saltar. Siempre ofrece "Cerrar sesión" para salir
 * sin aceptar.
 */
const ComplianceGate: React.FC<PropsWithChildren> = ({ children }) => {
    const { user, updateUser, logout } = useAuth();
    const needsProfile = !!user?.profile_incomplete;
    const pending: pendingDocument[] = user?.pending_documents ?? [];
    const hasDocuments = pending.length > 0;
    const gateRequired = needsProfile || hasDocuments;

    const [dob, setDob] = useState("");
    const [accepted, setAccepted] = useState<Record<string, boolean>>({});
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Fondo sólido mientras el gate está activo (coherente con el resto).
    useEffect(() => {
        if (!gateRequired) return;
        document.body.classList.add("solid-public-bg");
        return () => document.body.classList.remove("solid-public-bg");
    }, [gateRequired]);

    if (!gateRequired) return <>{children}</>;

    const toggle = (docType: string, value: boolean) =>
        setAccepted((prev) => ({ ...prev, [docType]: value }));

    const allAccepted = pending.every((d) => accepted[d.doc_type]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (needsProfile && !dob) {
            setError("Por favor ingresa tu fecha de nacimiento.");
            return;
        }
        if (!allAccepted) {
            setError("Debes aceptar todos los documentos para continuar.");
            return;
        }

        setSubmitting(true);
        try {
            if (needsProfile) await completeProfile(dob);
            for (const doc of pending) {
                await acceptPolicy(doc.doc_type);
            }
            updateUser({
                profile_incomplete: false,
                pending_documents: [],
                ...(needsProfile ? { date_of_birth: dob } : {}),
            });
        } catch {
            setError("Ocurrió un error al guardar. Intenta de nuevo.");
            setSubmitting(false);
        }
    };

    return (
        <div className={styles["gate-overlay"]}>
            <div className={styles["gate-card"]}>
                <h2 className={styles["gate-title"]}>
                    {hasDocuments
                        ? "Protección de tus datos personales"
                        : "Completa tu información"}
                </h2>
                <p className={styles["gate-subtitle"]}>
                    {hasDocuments
                        ? `Para usar la plataforma necesitamos que completes tu información y confirmes que conoces y aceptas los siguientes documentos, conforme a la Ley 1581 de 2012. Puedes leer cada documento haciendo clic en "Leer documento".`
                        : "Para continuar usando la plataforma, necesitamos tu fecha de nacimiento."}
                </p>

                <form onSubmit={handleSubmit} className={styles["gate-form"]}>
                    {needsProfile && (
                        <Input
                            id="dob"
                            label="Fecha de nacimiento"
                            type="date"
                            value={dob}
                            max={new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setDob(e.target.value)}
                        />
                    )}

                    {pending.map((doc) => (
                        <div key={doc.doc_type} className={styles["gate-doc-row"]}>
                            <label className={styles["gate-checkbox"]}>
                                <input
                                    type="checkbox"
                                    checked={!!accepted[doc.doc_type]}
                                    onChange={(e) => toggle(doc.doc_type, e.target.checked)}
                                />
                                <span>He leído y acepto el documento <strong>{doc.title}</strong>.</span>
                            </label>
                            {DOC_ROUTES[doc.doc_type] && (
                                <a
                                    className={styles["gate-doc-link"]}
                                    href={DOC_ROUTES[doc.doc_type]}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="Se abre en una pestaña nueva"
                                >
                                    Leer documento
                                    <ExternalLink size={14} />
                                </a>
                            )}
                        </div>
                    ))}

                    {error && <p className={styles["gate-error"]}>{error}</p>}

                    <Button
                        type="submit"
                        fullWidth
                        isLoading={submitting}
                        loadingText="Guardando..."
                        className={styles["gate-submit"]}
                    >
                        {hasDocuments ? "Aceptar y continuar" : "Guardar y continuar"}
                    </Button>

                    <button
                        type="button"
                        className={styles["gate-logout"]}
                        onClick={logout}
                        disabled={submitting}
                    >
                        Cerrar sesión
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ComplianceGate;
